<template>
  <div
    :id="'dropDownSelect-' + componentKey"
    :key="componentKey + redraw"
    v-click-outside="onClickOutside"
    :class="[
      {
        [$style.active]: isHighlight,
        [$style.wrap]: true,
        [$style.open]: isOpen && !disabled,
        [$style.disable]: disabled,
        [$style.readonly]: readonly,
        [$style.outline_none]: outline === 'none',
        [$style.outline_hover]: outline === 'hover',
        [$style.outline_always]: outline === 'always',
      }, $style[customClass], 'vgp-interactive-element',
    ]"
    :style="{
      'font-size': view.fontSize + 'px',
      'height': view.itemHeight ? view.itemHeight + 'px' : '32px'
    }"
  >
    <div
      :id="gpAutotestName || undefined"
      :data-testid="gpAutotestName || undefined"
      ref="selected_wrap"
      :class="{
        [$style.select_inner]: true,
        [$style.select_avatar]: itemTemplate === 'user',
      }"
      @click="onClickDropTrigger"
    >
      <template v-if="useSearch && isOpen">
        <sprite
          :name="'search'"
          :type="'regular'"
          :size="24"
          :class="[ $style.search_icon,]"
        />
        <input
          :id="'select-drop-trigger-' + componentKey"
          :data-testid="gpAutotestName ? gpAutotestName + '_search' : undefined"
          ref="search_input"
          v-model="searchTxt"
          :class="[$style.search_input]"
          :placeholder="searchTxt ? '' : locale.search"
          :style="{...selectedInputWidthStyle}"
          type="text"
        >
      </template>

      <template v-if="leftIcon">
        <sprite
          :name="leftIcon.name"
          :type="leftIcon.type"
          :size="leftIcon.size"
          :class="[ $style.left_icon,]"
        />
      </template>

      <div
        :class="[$style.selected_items, (useSearch && isOpen) ? $style.transparent : '']"
        :style="[
          { ...selectedInputWidthStyle },
          leftIcon ? { paddingLeft: `${leftIcon.size + 8}px`} : {},
        ]"
      >
        <span
          v-if="!localSelected.length && !stubTextIfAllUnselected"
          :class="[$style.placeholder_txt]"
        >
          {{ placeholder }}
        </span>

        <template v-if="multiselect">
          <template v-if="(isSelectedAllItems && stubTextIfAllSelected ) || (stubTextIfAllUnselected && isUnselectedAllItems)">
            <vgpChip
              v-if="isSelectedAllItems && dropItems.length"
              :gray-theme="true"
              :text="stubTextIfAllSelected"
              :preset="'text'"
            />
            <vgpChip
              v-if="isUnselectedAllItems"
              :gray-theme="true"
              :text="stubTextIfAllUnselected"
              :preset="'text'"
              :style="{width: 'fit-content'}"
            />
          </template>

          <template v-else>
            <vgpRollItems
              v-if="multiSelected.length"
              :key="componentKey + '-' + isOpen"
              :items="multiSelected"
              :offset="itemTemplate === 'user' ? -4 : 4"
            >
              <template #default="{item}">
                <vgpChip
                  :size="size"
                  :preset="(itemTemplate === 'user' && localSelected.length > 1) ? 'avatar' : itemTemplate"
                  :text="item[textPropertyName]"
                  :img-url="item[trackByImgUrl]"
                  :bg-color="item[colorPropertyName]"
                  :txt-color="item[trackByTextColor]"
                  :gray-theme="!['label', 'user', 'color'].includes(itemTemplate)"
                  :icon="item[trackByIconName]"
                  :fill-width="itemTemplate === 'color' ? false : localSelected.length === 1"
                  :status-color="item[trackByStatusColor]"
                  :need-description="needDescription"
                />
              </template>
            </vgpRollItems>
          </template>
        </template>

        <template v-if="!multiselect">
          <vgpChip
            v-if="singleSelected"
            :size="size"
            :preset="itemTemplate"
            :text="singleSelected[textPropertyName]"
            :img-url="singleSelected[trackByImgUrl]"
            :bg-color="singleSelected[colorPropertyName]"
            :txt-color="singleSelected[trackByTextColor]"
            :icon="singleSelected[trackByIconName]"
            :status-color="singleSelected[trackByStatusColor]"
            :txt-ellipsis="true"
            :fill-width="itemTemplate !== 'label'"
          />
        </template>
      </div>

      <div
        ref="reght_selected"
        :class="[$style.d_flex, $style.ai_center, $style.ml_8]"
      >
        <sprite
          v-show="isShowResetBtn"
          :name="'close-2'"
          :type="'bold'"
          :size="16"
          :class="[ $style.mr_8, 'vgp_select_reset_btn']"
          @click="onClickResetBtn"
        />

        <sprite
          v-if="dropArrowIcon"
          :id="gpAutotestName ? gpAutotestName + '_drop_arrow' : undefined"
          :data-testid="gpAutotestName ? gpAutotestName + '_drop_arrow' : undefined"
          :name="dropArrowIcon.name"
          :type="dropArrowIcon.type"
          :size="dropArrowIcon.size"
          :class="[ $style.drop_arrow, 'vgp_select_drop_arrow']"
          @click="toggleDropDown"
        />
      </div>
    </div>

    <div
      v-if="isOpen && !useColorPalette"
      :id="gpAutotestName ? gpAutotestName + '_drop' : undefined"
      :data-testid="gpAutotestName ? gpAutotestName + '_drop' : undefined"
      ref="drop"
      v-scroll.self="onDropListScroll"
      :class="[$style.drop, 'gantt_scroll scroll_visible']"
      :style="{
        ...popupStyles,
        'max-height': popupMaxHeight + 'px',
      }"
    >
      <div>
        <div :style="{ 'height': '8px'}" />

        <template v-if="showItems.length">
          <div
            v-if="multiselect && useSelectAll"
            :class="{
              [$style.border_bottom]: !dropListInfo.isTop,
              [$style.select_all_btn_wrap]: true
            }"
          >
            <div
              :id="gpAutotestName ? gpAutotestName + '_select_all' : undefined"
              :class="[$style.select_all_btn, $style.noselect]"
              @click="onSelectAllBtnClick"
            >
              <span>
                {{ isAllShowedItemsSelected ? locale.unselectAll : locale.selectAll }}
              </span>
            </div>
          </div>

          <div
            v-for="groupName in groupSet"
            :key="componentKey + groupName + dropWidth"
            :style="{width: dropWidth +'px', }"
          >
            <div
              v-if="groupName"
              :class="$style.group_devider"
              :style="{ color: groupViewSettings.color }"
            >
              <span :class="$style.group_devider_name"> {{ groupName }} </span>
              <div
                v-if="groupViewSettings.divider"
                :class="$style.group_devider_line"
              />
            </div>

            <div
              v-for="item in getItemsByGroupName(groupName)"
              :key="item[trackBy]"
              :id="gpAutotestName ? gpAutotestName + '_item_row_' + item[trackBy] : undefined"
              :class="[$style.drop_item, 'js-drop-item', trackByAdditionalForItem ? $style.drop_item_with_icon : null, item.disabled ? $style.disabled : '']"
              :title="useSystemTooltip ? item[textPropertyName] : ''"
            >
              <input
                v-if="multiselect"
                :id="multiselect + item[trackBy] + '-' + componentKey"
                v-model="multiSelected"
                type="checkbox"
                :value="item"
                :disabled="item.disabled"
              >
              <input
                v-else
                :id="multiselect + item[trackBy] + '-' + componentKey"
                v-model="singleSelected"
                type="radio"
                :value="item"
                :disabled="item.disabled"
              >

              <label
                v-if="tooltips(item)"
                v-tippy="tooltips(item)"
                :for="multiselect + item[trackBy] + '-' + componentKey"
                :class="checkboxAfter ? $style.checkboxAfter : $style.checkboxBefore"
                :style="[
                  { height: view.itemHeight ? view.itemHeight + 'px' : 'auto', width: dropWidth + 'px' },
                  item.disabled ? { color: '#CCCCCC' } : {}
                ]"
              >
                <vgpChip
                  :text="item[textPropertyName]"
                  :description="item.description"
                  :need-description="needDescription"
                  :img-url="item[trackByImgUrl]"
                  :bg-color="item[colorPropertyName]"
                  :txt-color="item[trackByTextColor]"
                  :status-color="item[trackByStatusColor]"
                  :preset="itemTemplate"
                  :size="size"
                  :icon="item[trackByIconName]"
                  :txt-ellipsis="true"
                  :selected="!multiselect && singleSelected.id === item.id"
                  :fill-width="itemTemplate !== 'label'"
                />

                <vgpChip
                  v-if="trackByAdditionalForItem && item[trackByAdditionalForItem]"
                  :text="item[trackByAdditionalForItem].text"
                  :description="item.description"
                  :need-description="needDescription"
                  :txt-color="item[trackByAdditionalForItem].txtColor"
                  :img-url="item[trackByAdditionalForItem].img-url"
                  :bg-color="item[trackByAdditionalForItem].bgColor"
                  :status-color="item[trackByAdditionalForItem].statusColor"
                  :preset="item[trackByAdditionalForItem].preset"
                  :round="item[trackByAdditionalForItem].round"
                  :padding-left="item[trackByAdditionalForItem].paddingLeft"
                  :padding-right="item[trackByAdditionalForItem].paddingRight"
                  :gray-theme="item[trackByAdditionalForItem].grayTheme"
                  :selected="!multiselect && singleSelected.id === item.id"
                  :size="size"
                  :icon="item[trackByAdditionalForItem].iconName"
                  :txt-ellipsis="true"
                  :style="{ marginLeft: 'auto' }"
                  :fill-width="itemTemplate !== 'label'"
                />

                <div
                  v-if="item.iconButtons"
                  :class="$style.icon_buttons"
                >
                  <icon-button
                    v-for="(button, index) in item.iconButtons"
                    :key="index"
                    :class="[$style.icon, singleSelected.id === item.id ? 'selected' : '']"
                    :icon="singleSelected.id === item.id ? { ...button.icon, color: '#fff'} : button.icon"
                    :size="button.size"
                    @onClick="button.handler(item)"
                  />
                </div>
              </label>

              <label
                v-else
                :for="multiselect + item[trackBy] + '-' + componentKey"
                :class="checkboxAfter ? $style.checkboxAfter : $style.checkboxBefore"
                :style="[
                  { 'height': view.itemHeight ? view.itemHeight + 'px' : 'auto', width: dropWidth + 'px' },
                  item.disabled ? { color: '#CCCCCC' } : {}
                ]"
              >
                <vgpChip
                  :description="item.description"
                  :need-description="needDescription"
                  :text="item[textPropertyName]"
                  :img-url="item[trackByImgUrl]"
                  :bg-color="item[colorPropertyName]"
                  :txt-color="item[trackByTextColor]"
                  :status-color="item[trackByStatusColor]"
                  :preset="itemTemplate"
                  :size="size"
                  :icon="item[trackByIconName]"
                  :txt-ellipsis="true"
                  :selected="!multiselect && singleSelected.id === item.id"
                  :fill-width="itemTemplate !== 'label'"
                />

                <vgpChip
                  v-if="trackByAdditionalForItem && item[trackByAdditionalForItem]"
                  :text="item[trackByAdditionalForItem].text"
                  :description="item.description"
                  :need-description="needDescription"
                  :txt-color="item[trackByAdditionalForItem].txtColor"
                  :img-url="item[trackByAdditionalForItem].imgUrl"
                  :bg-color="item[trackByAdditionalForItem].bgColor"
                  :status-color="item[trackByAdditionalForItem].statusColor"
                  :preset="item[trackByAdditionalForItem].preset"
                  :gray-theme="item[trackByAdditionalForItem].grayTheme"
                  :round="item[trackByAdditionalForItem].round"
                  :padding-left="item[trackByAdditionalForItem].paddingLeft"
                  :padding-right="item[trackByAdditionalForItem].paddingRight"
                  :size="size"
                  :icon="item[trackByAdditionalForItem].iconName"
                  :txt-ellipsis="true"
                  :selected="!multiselect && singleSelected.id === item.id"
                  :style="{ marginLeft: 'auto' }"
                  :fill-width="itemTemplate !== 'label'"
                />

                <div
                  v-if="item.iconButtons"
                  :class="[$style.icon_buttons, singleSelected.id === item.id ? $style.selected : '']"
                >
                  <icon-button
                    v-for="(button, index) in item.iconButtons"
                    :key="index"
                    :class="$style.button"
                    :icon="button.icon"
                    :size="button.size"
                    @onClick="button.handler(item)"
                  />
                </div>
              </label>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            :class="$style.no_search"
            :style="{width: popupStyles.width}"
          >
            <span> {{ noMatchesMessage }} </span>
          </div>
        </template>

        <div :style="{'height': '8px'}" />
      </div>
    </div>

    <context-menu
      v-if="isOpen && useColorPalette"
      :position="palettePosition"
    >
      <template #body>
        <colors-palette
          :colors="dropItems"
          :multiselect="true"
          :selected="localSelected"
          @selectColors="selectColor"
        />
      </template>
    </context-menu>
  </div>
</template>

<script>
import vgpChip from './includes/chip/vgpChip.vue';
import vgpRollItems from '../rollItems/vgpRollItems.vue';
import tooltip from '../tooltips/tooltip.vue';
import sprite from '../VueIcon/svgSprite.vue';
import iconButton from '../globalButton/withIcon/iconButton.vue';
import colorsPalette from '../colorPalette/colorsList/colorsList.vue';
import contextMenu from '../popups/contextMenuPopup/contextMenuPopup.vue';

const groupDefaultSettings = {
  divider: true,
  color: '#424242',
};

export default {
  name: 'VgpDropdownSelect',
  components: {
    vgpChip, vgpRollItems, tooltip, sprite, iconButton, colorsPalette, contextMenu,
  },
  props: {
    gpAutotestName: { type: String, required: false, default: '' },
    dropList: { type: Array, required: false, default: () => [] },
    selected: { type: [Array, Object], required: false, default: [] },
    multiselect: { type: Boolean, required: false, default: true },
    placeholder: { type: String, required: false, default: 'Placeholder' },
    groupBy: { type: String, required: false, default: '' },
    // deviderColor: { type: String, required: false, default: '#424242' },
    useSearch: { type: Boolean, required: false, default: false },
    useSelectAll: { type: Boolean, required: false, default: false },
    showSelectedFirst: { type: Boolean, required: false, default: true },
    trackBy: { type: String, required: false, default: 'id' },
    trackByText: { type: String, required: false, default: 'name' },
    trackByIconName: { type: String, required: false, default: 'icon' },
    trackByImgUrl: { type: String, required: false, default: 'img' },
    trackByBgColor: { type: String, required: false, default: 'color' },
    trackByStatusColor: { type: String, required: false, default: 'color' },
    trackByTextColor: { type: String, required: false, default: 'textColor' },
    popupMaxHeight: { type: Number, required: false, default: 240 },
    size: {
      type: String,
      required: false,
      default: 'sm',
      validator(value) {
        return ['sm', 'lg', 'autoWidth'].includes(value);
      },
    },
    itemTemplate: {
      type: String,
      required: false,
      default: 'text',
      validator(value) {
        return ['text', 'user', 'label', 'withImg', 'withIcon', 'status', 'color'].includes(value);
      },
    },
    showResetBtn: { type: Boolean, required: false, default: true },
    hideResetWhenSingleClosed: { type: Boolean, required: false, default: false },
    disabled: { type: Boolean, required: false, default: false },
    readonly: { type: Boolean, required: false, default: false },
    highlightActiveState: { type: Boolean, required: false, default: false },
    outline: {
      type: String,
      required: false,
      default: 'always',
      validator(value) {
        return ['none', 'always', 'hover'].includes(value);
      },
    },
    onlyOneCanSelected: { type: Array, required: false, default() { return []; } },
    dropPosition: {
      type: String,
      required: false,
      default: 'absolute',
      validator(value) {
        return ['fixed', 'absolute'].includes(value);
      },
    },
    trackByTooltipContent: { type: String, required: false, default: '' },
    tooltipPosition: { type: String, required: false, default: 'left' },
    parentSelectorForCalcPositionDrop: { type: String, required: false, default: '' },
    trackByAdditionalForItem: { type: String, required: false, default: '' },
    trackByItemTooltip: { type: String, required: false, default: '' },

    // instead of placeholder
    stubTextIfAllSelected: { type: String, required: false, default: '' },
    stubTextIfAllUnselected: { type: String, required: false, default: '' },

    useSystemTooltip: { type: Boolean, required: false, default: false },

    noMatchesMessage: { type: String, required: false, default: __('no_matches_message_text') },
    fixedWidth: { type: Number, required: false, default: 0 },
    needDescription: { type: Boolean, required: false, default: false },

    groupSettings: { type: Object, required: false, default: () => {} },
    leftIcon: { type: Object, required: false, default: null },
    arrowIcon: { type: Object, required: false, default: null },
    useColorPalette: { type: Boolean, required: false, default: false },
    customClass: { type: String, required: false, default: '' },
    checkboxAfter: { type: Boolean, required: false, default: false },
  },

  emits: {
    selected: [], // items[], emit after popup close if has changes selected item
    onChange: [], // items[], emit after every selected/unselected item
    onResetClick: null,
    onOpen: null,
    onClose: null,
  },

  data() {
    return {
      locale: {
        search: __('filter_search_placeholder'),
        selectAll: __('multicombo_select_all_items'),
        unselectAll: __('multicombo_unselect_all_items'),
        // items: __('tag_items'),
      },
      isOpen: false,
      searchTxt: '',
      showItems: [],
      dropListInfo: {
        isTop: true,
      },
      localSelected: [],
      dropWidth: 0,
      popupStyles: {},
      redraw: 0,
      elementOnTop: false,
      palettePosition: null,
    };
  },

  computed: {
    colors() {
      return this.$store.getters['teamSetting/colors'].data || [];
    },
    dropItems() {
      if (this.useColorPalette) {
        return this.colors;
      }

      return [...this.dropList];
    },

    colorPropertyName() {
      if (this.useColorPalette) {
        return 'hex';
      }

      return this.trackByBgColor;
    },

    textPropertyName() {
      if (this.useColorPalette) {
        return 'hex';
      }

      return this.trackByText;
    },

    selectedArray() {
      if (Array.isArray(this.selected)) {
        return [...this.selected];
      }

      if (this.selected) {
        return [this.selected];
      }

      return [];
    },

    multiSelected: {
      get() {
        return this.localSelected;
      },

      set(newSelected) {
        const preparedNewSelected = this.prepareNewMultiSelected(newSelected);

        this.localSelected = preparedNewSelected;
        this.emitChange(preparedNewSelected);
      },
    },

    singleSelected: {
      get() {
        return this.localSelected[0] || '';
      },

      set(newVal) {
        this.localSelected = [newVal];
        this.emitChange([newVal]);
        this.closePopup();
      },
    },

    componentKey() {
      return `drop_select_${new Date().getTime()}`;
    },

    selectedInputWidthStyle() {
      if (this.readonly) {
        return { width: '100%' };
      }

      let delta = 20;

      if (this.stubTextIfAllSelected && this.isAllShowedItemsSelected) return;

      this.isShowResetBtn && (delta += 40);
      this.useSearch && this.isOpen && (delta += 38);

      const width = `calc(100% - ${delta}px)`;

      return { width };
    },

    view() {
      switch (this.size) {
        case 'sm': return { fontSize: 14, itemHeight: 32 };
        case 'lg': return { fontSize: 16, itemHeight: 40 };
        case 'autoWidth': return { fontSize: 14 };
        default: return { fontSize: 14, itemHeight: 32 };
      }
    },

    isHighlight() {
      const highlightActive = this.highlightActiveState && (this.searchTxt || this.localSelected.length);

      return !this.disabled && (this.isOpen || highlightActive);
    },

    isShowResetBtn() {
      const length = this.localSelected.length;

      const case1 = this.multiselect && this.isOpen && this.useSearch && this.searchTxt;
      const case2 = this.multiselect && this.isOpen && !this.useSearch && length;
      const case3 = this.multiselect && !this.isOpen && length;
      const case4 = !this.multiselect && !this.isOpen && length && !this.hideResetWhenSingleClosed;
      const case5 = !this.multiselect && this.isOpen && this.useSearch && this.searchTxt;
      const case6 = !this.multiselect && this.isOpen && !this.useSearch && length;

      if ((length === this.dropItems.length) && this.stubTextIfAllSelected) return false;
      if (this.disabled) return false;

      return this.showResetBtn && (case1 || case2 || case3 || case4 || case5 || case6);
    },

    groupSet() {
      const set = [];

      this.showItems.forEach(item => {
        const groupName = (this.groupBy in item) ? item[this.groupBy] : null;

        if (!set.includes(groupName)) {
          groupName ? set.push(groupName) : set.unshift(groupName);
        }
      });

      return set;
    },

    isAllShowedItemsSelected() {
      if (this.localSelected.length < this.showItems.length) return false;

      const isAllSelected = this.showItems.every(item => this.localSelected.find(i => i[this.trackBy] === item[this.trackBy]));

      return isAllSelected;
    },

    isSelectedAllItems() {
      if (!this.localSelected) { return false; }

      if (!Array.isArray(this.localSelected)) { return false; }

      return this.localSelected.length === this.dropItems.length;
    },

    isUnselectedAllItems() {
      return !this.multiSelected.length && !this.singleSelected;
    },

    groupViewSettings() {
      return {
        ...groupDefaultSettings,
        ...this.groupSettings,
      };
    },

    dropArrowIcon() {
      if (this.arrowIcon) {
        return {
          name: this.arrowIcon.name,
          type: this.arrowIcon.type,
          size: this.arrowIcon.size,
        };
      }

      return {
        name: 'drop-down',
        type: 'bold',
        size: 24,
      };
    },
  },

  watch: {
    selectedArray: {
      handler(val) {
        let selected = val;

        if (this.useColorPalette) {
          selected = this.prepareColorItemsIfNeeded(val);
        }

        this.localSelected = selected;
      },
      immediate: true,
    },
    trackByIconName() {
      this.redraw++;
    },
    isOpen(val, oldVal) {
      if (val) {
        this.$nextTick(() => {
          this.sortShowedItemsBySelected();
          this.$refs.search_input && this.$refs.search_input.focus();
          this.checkPopupPosition();
          this.$emit('onOpen');
        });
      } else {
        this.searchTxt = '';
        this.$emit('onClose');
      }
    },

    searchTxt(val) {
      this.updateShowedItemsBySearch();
    },

    showItems: {
      handler(val) {
        if (this.isOpen) {
          this.$nextTick(() => {
            this.checkPopupPosition();
          });
        }
      },
      deep: false,
    },

    dropItems(data) {
      if (data.length) {
        const localSelected = [];

        data.forEach(item => {
          const isSelected = this.localSelected.find(s => s.id === item.id);
          const index = this.localSelected.indexOf(isSelected);

          if (index !== -1) {
            localSelected.push(item);
          }
        });

        if (this.localSelected.length !== localSelected.length) {
          this.emitChange(localSelected);
          this.emitSelected(localSelected);
        }

        this.localSelected = localSelected;
      }
      this.updateShowedItemsBySearch();
    },
  },

  beforeMount() {
    this.showItems = [...this.dropItems];
  },

  mounted() {
    this.addListheners();

    if (this.fixedWidth) {
      this.dropWidth = this.fixedWidth;
    }
  },

  beforeUnmount() {
    this.removeListheners();
  },

  methods: {
    emitChange(val) {
      this.$emit('onChange', [...val]);
    },
    emitSelected(val) {
      this.$emit('selected', [...val]);
    },
    selectColor(selectedColors) {
      this.multiSelected = [...selectedColors];
    },
    prepareNewMultiSelected(newSelected) {
      if (!this.onlyOneCanSelected || !this.onlyOneCanSelected.length) {
        return newSelected;
      }

      const onlyOneIds = this.onlyOneCanSelected ? this.onlyOneCanSelected.map(i => i[this.trackBy]) : [];
      const onlyOneAlreadySelected = this.localSelected.find(item => onlyOneIds.includes(item[this.trackBy]));
      const isOnlyOneExistInNewSelected = newSelected.some(item => onlyOneIds.includes(item[this.trackBy]));

      if (isOnlyOneExistInNewSelected && !onlyOneAlreadySelected) {
        return [...this.onlyOneCanSelected];
      }

      if (onlyOneAlreadySelected) {
        return newSelected.filter(item => item[this.trackBy] !== onlyOneAlreadySelected[this.trackBy]);
      }

      return newSelected;
    },
    prepareColorItemsIfNeeded(val) {
      const values = Array.isArray(val) ? val : [val];
      const preparedColors = [];

      if (values.some(item => typeof item === 'number' || typeof item === 'string')) {
        values.forEach(id => {
          const colorItem = this.colors.find(c => c.id === +id);

          if (colorItem) {
            preparedColors.push(colorItem);
          }
        });
      }

      return preparedColors;
    },
    tooltips(item) {
      const tooltip = item[this.trackByItemTooltip];

      if (!tooltip || !tooltip.content) {
        return;
      }

      return {
        placement: tooltip.placement ? tooltip.placement : 'left',
        interactive: tooltip.interactive ? tooltip.interactive : false,
        content: tooltip.content,
        delay: 0,
        duration: [0, 0],
        theme: tooltip.theme ? tooltip.theme : '',
        maxWidth: 250,
      };
    },

    closePopup() {
      if (!this.isOpen) return;

      // if (this.isHasChangesInSelected()) {
      //   this.emitSelected([...this.localSelected]);
      // }

      this.emitSelected([...this.localSelected]);
      this.isOpen = false;
      this.palettePosition = null;
    },

    isHasChangesInSelected() {
      if (this.selectedArray.length !== this.localSelected.length) return true;

      const ids = this.selectedArray.map(i => i[this.trackBy]);
      const every = this.localSelected.every(i => ids.includes(i[this.trackBy]));

      return !every;
    },

    addListheners() {
      const divs = [
        ...document.querySelectorAll('div.gantt_scroll'),
        ...document.querySelectorAll('div.gantt_scroll .v-table__wrapper'),
        ...document.querySelectorAll('div.vgp_scroll_bar_container .simplebar-content-wrapper'),
      ];

      divs.forEach(div => {
        div.addEventListener('scroll', this.onScroll);
      });

      document.body.addEventListener('keydown', this.handleKeydown);
    },

    removeListheners() {
      const divs = [
        ...document.querySelectorAll('div.gantt_scroll'),
        ...document.querySelectorAll('div.gantt_scroll .v-table__wrapper'),
        ...document.querySelectorAll('div.vgp_scroll_bar_container .simplebar-content-wrapper'),
      ];

      divs.forEach(div => {
        div.removeEventListener('scroll', this.onScroll);
      });

      document.body.removeEventListener('keydown', this.handleKeydown);
    },

    onScroll(e) {
      if (!this.isOpen) return;

      if (!e.target.closest(`#${this.componentKey}`)) {
        this.isOpen = false;
      }
    },

    onClickDropTrigger(e) {
      const path = e.path ? e.path : e.composedPath(e.target);

      if (!this.$refs.selected_wrap) {
        return;
      }

      const coords = this.$refs.selected_wrap.getBoundingClientRect();
      const elementOnTop = document.elementFromPoint(coords.left, coords.bottom);
      const elementOnTopRect = elementOnTop.getBoundingClientRect();

      this.palettePosition = coords;

      if (elementOnTopRect && coords.bottom > elementOnTopRect.top && coords.bottom < elementOnTopRect.bottom - 2) {
        this.elementOnTop = true;
      } else {
        this.elementOnTop = false;
      }

      const isDropArrow = path.find(elem => {
        if (!elem.classList) return;

        return elem.classList.contains('vgp_select_drop_arrow') || elem.classList.contains('vgp_select_reset_btn');
      });

      if (isDropArrow) return;

      if (!this.multiselect) {
        this.toggleDropDown();

        return;
      }

      // if (this.multiselect && !this.isOpen) {
      //   this.toggleDropDown();
      // }

      if (this.multiselect) {
        this.toggleDropDown();
      }
    },

    onClickResetBtn() {
      if (this.isOpen && this.useSearch) {
        this.searchTxt = '';
        this.$refs.search_input && this.$refs.search_input.focus();

        return;
      }

      this.$emit('onResetClick');
      this.emitChange([]);
      this.emitSelected([]);
      this.localSelected = [];
    },

    getItemsByGroupName(name) {
      if (name === null) {
        return this.showItems.filter(item => !(this.groupBy in item));
      }

      return this.showItems.filter(item => item[this.groupBy] === name);
    },

    sortShowedItemsBySelected() {
      if (!this.showSelectedFirst) return;

      const res = [];

      this.groupSet.forEach(name => {
        const first = []; const
          second = [];
        const group = this.getItemsByGroupName(name);

        group.forEach(item => {
          if (this.localSelected.find(i => i[this.trackBy] === item[this.trackBy])) {
            first.push(item);
          } else second.push(item);
        });

        res.push(...first, ...second);
      });

      this.showItems = res;
    },

    updateShowedItemsBySearch() {
      if (!this.searchTxt) {
        this.showItems = [...this.dropItems];

        return;
      }

      const str = this.searchTxt.toLowerCase();

      this.showItems = this.dropItems.filter(item => {
        const value = item[this.textPropertyName].toLowerCase();

        if (value.includes(str)) {
          return true;
        }

        const additional = item[this.trackByAdditionalForItem];

        if (!additional || !additional.useInSearch) { return false; }

        return additional.text.toLowerCase().includes(str);
      });
    },

    onSelectAllBtnClick() {
      if (!this.multiselect) return;

      if (this.isAllShowedItemsSelected) {
        this.localSelected = this.localSelected.filter(item => !this.showItems.find(i => i[this.trackBy] === item[this.trackBy]));
      } else {
        this.localSelected = [...this.showItems];
      }

      this.emitChange(this.localSelected);
      this.emitSelected(this.localSelected);
    },

    onDropListScroll(e) {
      if (this.$refs.drop.scrollTop === 0) {
        this.dropListInfo.isTop = true;
      } else {
        this.dropListInfo.isTop = false;
      }

      const el = this.trackByItemTooltip && document.querySelector('[data-tippy-root]');

      if (el && el._tippy && el._tippy.state?.isVisible) {
        el._tippy.hide();
      }
    },

    onClickOutside(e) {
      this.closePopup();
    },

    toggleDropDown() {
      this.isOpen ? this.closePopup() : this.openPopup();
    },

    openPopup() {
      this.updateShowedItemsBySearch();
      this.isOpen = true;
    },

    checkPopupPosition() {
      if (this.dropPosition === 'fixed') {
        this.checkPopupPositionFixed();
      } else {
        this.checkPopupPositionAbsolute();
      }
    },

    checkPopupPositionAbsolute() {
      const dropPopup = this.$refs.drop;

      if (!dropPopup) {
        return;
      }

      const coords = this.$refs.selected_wrap.getBoundingClientRect();

      const componentWidth = coords.width;

      const clientHeight = document.body.clientHeight;
      const dropHeight = this.$refs.drop.offsetHeight;

      const canShowBottom = (coords.bottom + dropHeight) < (clientHeight - 30);
      const canShowTop = (coords.top - dropHeight) > 10;
      const dropToBottom = (!canShowBottom && !canShowTop) || canShowBottom;

      this.popupStyles = {
        position: 'absolute',
        width: `${coords.width}px`,
        top: dropToBottom ? `${coords.height + 5}px` : `-${dropHeight + 5}px`,
      };

      this.$nextTick(() => {
        const dropWidthEl = this.$refs.drop.scrollWidth;

        if (this.fixedWidth) {
          this.dropWidth = dropWidthEl > this.fixedWidth ? dropWidthEl : this.fixedWidth;
        } else {
          this.dropWidth = dropWidthEl > componentWidth ? componentWidth : dropWidthEl;
        }
      });
    },

    checkPopupPositionFixed() {
      const dropPopup = this.$refs.drop;

      if (!dropPopup) {
        return;
      }

      const parent = this.parentSelectorForCalcPositionDrop && document.querySelector(`${this.parentSelectorForCalcPositionDrop}`);
      const parentCoords = parent ? parent.getBoundingClientRect() : null;

      const targetSelect = this.$refs.selected_wrap.getBoundingClientRect();
      const width = targetSelect.width;
      const dropdownHeight = this.$refs.drop.offsetHeight;
      const clientHeight = document.documentElement.clientHeight;
      const clientWidth = document.documentElement.clientWidth;
      const bottomDiff = clientHeight - targetSelect.bottom;
      const border = 2;

      const defaultPosition = {
        top: 'auto',
        left: 'auto',
        bottom: 'auto',
        right: 'auto',
      };

      let styles = {};

      if (bottomDiff < dropdownHeight) {
        styles = {
          ...defaultPosition,
          top: 'auto',
          bottom: `${clientHeight - targetSelect.top - (parentCoords ? parentCoords.top : 0) + border + 3}px`,
        };
      } else {
        styles = {
          ...defaultPosition,
          top: `${targetSelect.bottom - (parentCoords ? parentCoords.top : 0) + border + 3}px`,
          bottom: 'auto',
        };
      }

      if (clientWidth < targetSelect.left + this.fixedWidth) {
        styles.right = `${clientWidth - targetSelect.right}px`;
      }

      this.popupStyles = {
        position: 'fixed',
        width: `${width}px`,
        ...styles,
      };

      this.$nextTick(() => {
        const dropWidthEl = this.$refs.drop.scrollWidth;

        if (this.fixedWidth) {
          this.dropWidth = dropWidthEl > this.fixedWidth ? dropWidthEl : this.fixedWidth;
        } else {
          this.dropWidth = dropWidthEl > width ? width : dropWidthEl;
        }
      });
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closePopup();
      }
    },

    // checkPopupPositionFixed() {
    //   const dropPopup = this.$refs.drop;

    //   if (!dropPopup) {
    //     return;
    //   }

    //   const coords = this.$refs.selected_wrap.getBoundingClientRect();
    //   const parent = document.querySelector(`${this.parentSelectorForCalcPositionDrop}`);
    //   let parentCoords = null;

    //   if (parent) {
    //     parentCoords = parent.getBoundingClientRect();
    //   }

    //   const componentWidth = coords.width;

    //   const clientHeight = document.body.clientHeight;
    //   const clientWidth = document.body.clientWidth;
    //   const dropHeight = this.$refs.drop.offsetHeight;

    //   const canShowBottom = clientHeight - coords.bottom - dropHeight > 30;
    //   const canShowTop = coords.top - dropHeight > 10;
    //   const dropToBottom = (!canShowBottom && !canShowTop) || canShowBottom;
    //   const topPosition = `${coords.top - dropHeight - (parentCoords ? parentCoords.top : 0) - 2}px`;
    //   const bottomPosition = `${coords.bottom - (parentCoords ? parentCoords.top : 0) + 5}px`;
    //   let top = null;
    //   let right = null;

    //   if (parent && this.elementOnTop && canShowTop) {
    //     top = topPosition;
    //   } else {
    //     top = dropToBottom ? bottomPosition : topPosition;
    //   }

    //   if (clientWidth < coords.left + this.fixedWidth) {
    //     right = clientWidth - coords.right;
    //   }

    //   this.popupStyles = {
    //     position: 'fixed',
    //     width: `${coords.width}px`,
    //     top,
    //     right: `${right}px`,
    //   };

    //   this.$nextTick(() => {
    //     const dropWidthEl = this.$refs.drop.scrollWidth;

    //     if (this.fixedWidth) {
    //       this.dropWidth = dropWidthEl > this.fixedWidth ? dropWidthEl : this.fixedWidth;
    //     } else {
    //       this.dropWidth = dropWidthEl > componentWidth ? componentWidth : dropWidthEl;
    //     }
    //   });
    // },
  },
};
</script>

<style module src="./dropSelect.less" lang="less"></style>
